import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import {
  makeStyles, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Chip, TextField
} from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { useQuery } from '@tanstack/react-query'
import SydCheckbox from '../../../../commonDesign/SydCheckbox'
import { useSearchPermissions } from '../../../../../api/users'
import { fetchUsers } from '../../../../../service'
import { FEATURE_FLAG } from '../../../../../constants'

const useStyles = makeStyles((theme) => ({
  section: {
    marginBottom: theme.spacing(2)
  },
  sectionTitle: {
    fontSize: '14px',
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: 'var(--dai-color-text-low)'
  },
  flagRow: {
    display: 'flex',
    gap: theme.spacing(3),
    alignItems: 'center'
  },
  flagItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5)
  },
  chipContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1)
  }
}))

const FEATURE_OPTIONS = Object.keys(FEATURE_FLAG).sort()

function buildAudienceSummary (audience) {
  if (!audience) return 'All users'
  const parts = []
  if (audience.isAdmin) parts.push('Admin')
  if (audience.isInternal) parts.push('Internal')
  if (audience.isSummit) parts.push('Summit')
  if (audience.permissions?.length > 0) parts.push(`${audience.permissions.length} permission(s)`)
  if (audience.features?.length > 0) parts.push(`${audience.features.length} feature(s)`)
  if (audience.userIds?.length > 0) parts.push(`${audience.userIds.length} user(s)`)
  return parts.length > 0 ? parts.join(', ') : 'All users'
}

function AudienceModal ({ open, onClose, audience, onSave }) {
  const classes = useStyles()

  const [isAdmin, setIsAdmin] = useState(false)
  const [isInternal, setIsInternal] = useState(false)
  const [isSummit, setIsSummit] = useState(false)
  const [permissions, setPermissions] = useState([])
  const [features, setFeatures] = useState([])
  const [selectedUsers, setSelectedUsers] = useState([])
  const [permSearch, setPermSearch] = useState('')
  const [userSearchInput, setUserSearchInput] = useState('')
  const [debouncedUserSearch, setDebouncedUserSearch] = useState('')
  const debounceRef = useRef(null)

  const handleUserSearchChange = useCallback((val) => {
    setUserSearchInput(val)
    clearTimeout(debounceRef.current)
    if (val.length < 2) {
      setDebouncedUserSearch('')
      return
    }
    debounceRef.current = setTimeout(() => setDebouncedUserSearch(val), 350)
  }, [])

  useEffect(() => {
    return () => clearTimeout(debounceRef.current)
  }, [])

  const { data: permissionsData } = useSearchPermissions(
    { skip: 0, take: 500 }
  )

  const { data: usersData } = useQuery({
    queryKey: ['audience-user-search', debouncedUserSearch],
    queryFn: async () => {
      const { data } = await fetchUsers({
        searchFields: ['firstName', 'lastName', 'email'],
        searchText: debouncedUserSearch,
        limit: 20,
        offset: 0
      })
      return data
    },
    enabled: debouncedUserSearch.length >= 2
  })

  const permissionOptions = useMemo(() => {
    return (permissionsData || []).map(p => p.permissionId || p.name || p)
  }, [permissionsData])

  const userOptions = useMemo(() => {
    if (!usersData?.users) return []
    return usersData.users.map(u => {
      const name = `${u.firstName || ''} ${u.lastName || ''}`.trim()
      return {
        userId: u.internalId,
        label: name ? `${name} (${u.internalId})` : `User ${u.internalId}`
      }
    })
  }, [usersData])

  useEffect(() => {
    if (open) {
      setIsAdmin(audience?.isAdmin === true)
      setIsInternal(audience?.isInternal === true)
      setIsSummit(audience?.isSummit === true)
      setPermissions(audience?.permissions || [])
      setFeatures(audience?.features || [])
      setSelectedUsers((audience?.userIds || []).map(id => ({ userId: id, label: `User ${id}` })))
      setPermSearch('')
      setUserSearchInput('')
      setDebouncedUserSearch('')
    }
  }, [open, audience])

  const handleSave = useCallback(() => {
    const result = {}
    if (isAdmin) result.isAdmin = true
    if (isInternal) result.isInternal = true
    if (isSummit) result.isSummit = true
    if (permissions.length > 0) result.permissions = permissions
    if (features.length > 0) result.features = features
    if (selectedUsers.length > 0) result.userIds = selectedUsers.map(u => u.userId)

    onSave(Object.keys(result).length > 0 ? result : undefined)
    onClose()
  }, [isAdmin, isInternal, isSummit, permissions, features, selectedUsers, onSave, onClose])

  const handleRemovePermission = useCallback((perm) => {
    setPermissions(prev => prev.filter(p => p !== perm))
  }, [])

  const handleRemoveFeature = useCallback((feat) => {
    setFeatures(prev => prev.filter(f => f !== feat))
  }, [])

  const handleRemoveUser = useCallback((uid) => {
    setSelectedUsers(prev => prev.filter(u => u.userId !== uid))
  }, [])

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Audience</DialogTitle>
      <DialogContent>
        <div className={classes.section}>
          <div className={classes.sectionTitle}>User Flags</div>
          <div className={classes.flagRow}>
            <div className={classes.flagItem}>
              <SydCheckbox value={isAdmin} onChange={(e, c) => setIsAdmin(c)} size='small' />
              <span>Admin</span>
            </div>
            <div className={classes.flagItem}>
              <SydCheckbox value={isInternal} onChange={(e, c) => setIsInternal(c)} size='small' />
              <span>Internal</span>
            </div>
            <div className={classes.flagItem}>
              <SydCheckbox value={isSummit} onChange={(e, c) => setIsSummit(c)} size='small' />
              <span>Summit</span>
            </div>
          </div>
        </div>

        <div className={classes.section}>
          <div className={classes.sectionTitle}>Permissions</div>
          <Autocomplete
            options={permissionOptions}
            inputValue={permSearch}
            onInputChange={(e, val) => setPermSearch(val || '')}
            onChange={(e, val) => {
              if (val && !permissions.includes(val)) {
                setPermissions(prev => [...prev, val])
              }
              setPermSearch('')
            }}
            renderInput={(params) => (
              <TextField {...params} variant='outlined' size='small' placeholder='Search permissions...' />
            )}
            value={null}
          />
          {permissions.length > 0 && (
            <div className={classes.chipContainer}>
              {permissions.map(p => (
                <Chip key={p} label={p} size='small' onDelete={() => handleRemovePermission(p)} />
              ))}
            </div>
          )}
        </div>

        <div className={classes.section}>
          <div className={classes.sectionTitle}>Features</div>
          <Autocomplete
            options={FEATURE_OPTIONS.filter(f => !features.includes(f))}
            onChange={(e, val) => {
              if (val && !features.includes(val)) {
                setFeatures(prev => [...prev, val])
              }
            }}
            renderInput={(params) => (
              <TextField {...params} variant='outlined' size='small' placeholder='Search features...' />
            )}
            value={null}
          />
          {features.length > 0 && (
            <div className={classes.chipContainer}>
              {features.map(f => (
                <Chip key={f} label={f} size='small' onDelete={() => handleRemoveFeature(f)} />
              ))}
            </div>
          )}
        </div>

        <div className={classes.section}>
          <div className={classes.sectionTitle}>Specific Users</div>
          <Autocomplete
            options={userOptions}
            getOptionLabel={(opt) => opt.label || ''}
            getOptionSelected={(opt, val) => opt.userId === val.userId}
            inputValue={userSearchInput}
            onInputChange={(e, val, reason) => {
              if (reason === 'reset') return
              handleUserSearchChange(val || '')
            }}
            onChange={(e, val) => {
              if (val?.userId && !selectedUsers.some(u => u.userId === val.userId)) {
                setSelectedUsers(prev => [...prev, val])
              }
              handleUserSearchChange('')
            }}
            renderInput={(params) => (
              <TextField {...params} variant='outlined' size='small' placeholder='Search users (min 2 chars)...' />
            )}
            value={null}
            filterOptions={(x) => x}
          />
          {selectedUsers.length > 0 && (
            <div className={classes.chipContainer}>
              {selectedUsers.map(u => (
                <Chip key={u.userId} label={u.label} size='small' onDelete={() => handleRemoveUser(u.userId)} />
              ))}
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} color='primary' variant='contained'>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

AudienceModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  audience: PropTypes.object,
  onSave: PropTypes.func.isRequired
}

export { buildAudienceSummary }
export default AudienceModal
